import React, { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/BaseUrl';
import ExternalRoutes from '../config/ExternalRoutes';
import { MessageContext } from '../context/MessageContext';
import { useNavigate } from 'react-router';
import useAxiosRequests from '../hooks/useAxiosRequest';
import pdfImage from "../img/pdf.png";
import { CircularProgress } from '@mui/material';

export const EditDocument = () => {
  const location = useLocation();
  const { documents, selectedFile } = location.state || {};
  const [newFile, setNewFile] = useState(selectedFile || null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(MessageContext);
  const { sendRequest } = useAxiosRequests();
  const [loading, setLoading] = useState(false); 

  useEffect(() => {
    if (selectedFile) {
      const preview = URL.createObjectURL(selectedFile);
      setPreviewUrl(preview);

      return () => {
        if (preview) {
          URL.revokeObjectURL(preview);
        }
      };
    }
  }, [selectedFile]);


  const validationSchema = Yup.object({
    file: Yup.mixed()
      .nullable()
      .test('fileSize', 'Le fichier est trop volumineux (max 5MB)', (value) => {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024; // Limite de 5MB
      })
      .test('fileType', 'Format de fichier non pris en charge', (value) => {
        if (!value) return true;
        return ['image/jpeg', 'image/png', 'application/pdf'].includes(value.type);
      }),
  });

  const handleProcess = async (values) => {
    setLoading(true);
    const { file } = values;

    const formData = new FormData();
    if (file) {
      formData.append('file', file);
    }

    try {
      const response = await sendRequest({
        method: 'POST',
        url: `${ExternalRoutes.document.edit}${documents?.id}`,
        body: formData,
      });


      if (response.status === 'error') {
        throw new Error(response.message || "Une erreur est survenue lors de la modification.");
      }

      showSuccess(response.message || "Image mise à jour avec succès.");
      navigate('/');
    } catch (error) {
      showError(error.message || "Une erreur est survenue lors de la modification.");
    }finally{
    setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      file: newFile || null,
    },
    validationSchema,
    onSubmit: handleProcess,
  });

  return (
    <section>
      <div className="kl-header-document-document">
        <div className="kl-header-coffre">
          <button onClick={() => window.history.back()} className="p-0 kl-back-button">
            <FontAwesomeIcon icon={faArrowLeft} className="kl-icon-arrow" />
          </button>
          <div className="kl-title-header">
            <h2>{documents?.docName}</h2>
          </div>
          <div>
            <button
              className="kl-edit-doc-svg"
              onClick={() => document.getElementById('fileInput').click()}
            >
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.53999 18.0938C1.92999 18.0938 1.35999 17.8838 0.949987 17.4938C0.429987 17.0038 0.179986 16.2638 0.269986 15.4638L0.639987 12.2238C0.709987 11.6138 1.07999 10.8038 1.50999 10.3638L9.71999 1.67378C11.77 -0.496221 13.91 -0.55622 16.08 1.49378C18.25 3.54378 18.31 5.68378 16.26 7.85378L8.04999 16.5438C7.62999 16.9938 6.84999 17.4138 6.23999 17.5138L3.01999 18.0638C2.84999 18.0738 2.69999 18.0938 2.53999 18.0938ZM12.93 1.48378C12.16 1.48378 11.49 1.96378 10.81 2.68378L2.59999 11.3838C2.39999 11.5938 2.16999 12.0938 2.12999 12.3838L1.75999 15.6238C1.71999 15.9538 1.79999 16.2238 1.97999 16.3938C2.15999 16.5638 2.42999 16.6238 2.75999 16.5738L5.97999 16.0238C6.26999 15.9738 6.74999 15.7138 6.94999 15.5038L15.16 6.81378C16.4 5.49378 16.85 4.27378 15.04 2.57378C14.24 1.80378 13.55 1.48378 12.93 1.48378Z" fill="#111827" />
                <path d="M14.3404 9.52382C14.3204 9.52382 14.2904 9.52382 14.2704 9.52382C11.1504 9.21382 8.64044 6.84382 8.16044 3.74382C8.10044 3.33382 8.38044 2.95382 8.79044 2.88382C9.20044 2.82382 9.58044 3.10382 9.65044 3.51382C10.0304 5.93382 11.9904 7.79382 14.4304 8.03382C14.8404 8.07382 15.1404 8.44382 15.1004 8.85382C15.0504 9.23382 14.7204 9.52382 14.3404 9.52382Z" fill="#111827" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: "85px", paddingTop: "105px" }}>
        <div className="kl-display-image-facture">
          {previewUrl ? (
            <img
              src={newFile.type === "application/pdf" ? pdfImage : previewUrl}
              alt="aperçu"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          ) : documents?.file?.flUrl ? (
            <img
              src={`${API_BASE_URL}${documents.file.flUrl}`}
              alt="document"
              aria-hidden="true"
              onError={() => setImageError(true)}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          ) : (
            <p>Aucun fichier disponible.</p>
          )}
          {imageError && <p>Erreur lors du chargement de l'image.</p>}
        </div>
      </div>

      <input
        id="fileInput"
        name="file"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setNewFile(file);
            formik.setFieldValue('file', file);

            const preview = URL.createObjectURL(file);
            setPreviewUrl(preview);
          } else {
            formik.setFieldValue('file', null);
          }
        }}
        onBlur={formik.handleBlur}
      />

      <div className='kl-button-subscription-content'>
        <div className='kl-button-start-sub'>
          <button type='submit'
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting}>
            {loading?
            <>
              <CircularProgress size={15} color='inherit' />
              <span className='mx-2'>Enregistration des modifications...</span>
            </>
            :
            "Enregistrer les modifications"
            }
          </button>
        </div>
      </div>

      {/* Affichage des erreurs */}
      {formik.touched.file && formik.errors.file ? (
        <div className="error">{formik.errors.file}</div>
      ) : null}
    </section>
  );
};